import { ComboBox, Icon, ProgressIndicator, Toggle } from "@fluentui/react";
import { useStore } from "@taskpane/hooks";
import { setColor } from "@taskpane/theme";
import { AccountNature, comboBoxHeight, comboBoxWidth } from "@taskpane/types";
import { onFormatDate, onParseDateFromString, setApiURL } from "@taskpane/utils";
import { standardDateFormat } from "@taskpane/types";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import BalanceSheet from "../../features/balanceSheetPL/balanceSheet";
import { PMDatePicker } from "@taskpane/components";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { Endpoints } from "src/taskpane/core/endpoints";

interface BalanceSheetWizardProps {
  onBackClick: () => void;
}

export const BalanceSheetWizard = (props: BalanceSheetWizardProps) => {
  const { t } = useTranslation();
  const { token } = useStore();

  const [companyList, setCompanyList] = useState([]);
  const [companyListOptions, setCompanyListOptions] = useState([]);
  const [selCompanyKey, setSelCompanyKey] = useState("");
  const [selCompanyName, setSelCompanyName] = useState("");
  const [selFiscalYear, setSelFiscalYear] = useState("");
  const [selCompanyError, setSelCompanyError] = useState(null);
  //
  const [accountListOptions, setAccountListOptions] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const [fromAccountError, setFromAccountError] = useState(null);
  const [toAccountError, setToAccountError] = useState(null);
  //
  const [currencyListOptions, setCurrencyListOptions] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [currency, setCurrency] = useState("");
  const [currencyError, setCurrencyError] = useState(null);
  //
  const [asOnDate, setAsOnDate] = useState(new Date());
  const [asOnDateError, setAsOnDateError] = useState(null);
  //
  const [originalCurrency, setOriginalCurrency] = useState(false);
  const [withZeroAccounts, setWithZeroAccounts] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  function handleBackOnClick() {
    props.onBackClick();
  }

  useEffect(() => {
    const fetchFiscalYears = async () => {
      const url = setApiURL(Endpoints.getFiscalYears());
      const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        };
        return response.json();
      });
      setCompanyList(val);
    }
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    if (selCompanyKey !== "") {
      const fetchAccountList = async () => {
        const url = setApiURL(Endpoints.getCOA(selCompanyName, selFiscalYear));
        const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          };
          return response.json();
        });
        setAccountList(val);
      }
      fetchAccountList();

      const fetchCurrencyList = async () => {
        const url = setApiURL(Endpoints.getCurrencies(selCompanyName, selFiscalYear));
        const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          };
          return response.json();
        });
        setCurrencyList(val);
      }
      fetchCurrencyList();
    }
  }, [selCompanyKey]);

  const validate = () => {
    let result = true;
    if (!selCompanyKey.trim()) {
      setSelCompanyError(t("pleaseChooseValue"));
      result = false;
    }

    return result;
  };

  const onCompanyChange = (event, option, index, value) => {
    setSelCompanyKey(option.key);
    const company = companyList.find((c) => c.ID === option.key);
    if (company) {
      setSelCompanyName(company.CompanyName);
      setSelFiscalYear(company.FiscalYearCode);
      setAsOnDate(moment(company.EndDate).toDate());

      // dispatch(loadAccountList(company.CompanyName, company.FiscalYearCode));
      // dispatch(loadCurrencyList(company.CompanyName, company.FiscalYearCode));
    }
  };

  useEffect(() => {
    if (accountList && accountList.length > 0) {
      setAccountListOptions(
        accountList
          .filter((e) => e.AccountNature === AccountNature.Assets || e.AccountNature === AccountNature.Liabilities)
          .map((e) => ({ key: e.AccountNumber, text: e.DisplayFormatedString }))
      );
    }
  }, [accountList]);

  useEffect(() => {
    if (currencyList && currencyList.length > 0) {
      setCurrencyListOptions(currencyList.map((e) => ({ key: e.Code, text: e.Code })));
    }
  }, [currencyList]);

  // useEffect(() => {
  //   dispatch(loadCompanyList(true));
  // }, [dispatch]);

  useEffect(() => {
    setCompanyListOptions(companyList.map((element) => ({ key: element.ID, text: element.FiscalYearDescription, companyName: element.CompanyName, fiscalYear: element.FiscalYearCode })));
  }, [companyList]);

  async function onOkClick() {
    if (!validate()) return;
    try {
      setIsValidating(true);

      await Excel.run(async (context) => {
        try {
          const calc = new BalanceSheet();

          const company = companyList.find((c) => c.ID === selCompanyKey);
          if (company) {
            let actualFromAccount = fromAccount;
            let actualToAccount = toAccount;

            if (accountList && accountList.length > 0) {
              if (!actualFromAccount && accountList && accountList.length > 0) {
                actualFromAccount = accountList[0].AccountNumber;
              }
              if (!actualToAccount && accountList) {
                actualToAccount = accountList[accountList.length - 1].AccountNumber;
              }
            }


            const list = await fetch(setApiURL(Endpoints.getBalanceSheetTree(company.CompanyName, company.FiscalYearCode)) + "?" + new URLSearchParams({
              as_on: moment(asOnDate).format(standardDateFormat),
              from_account: actualFromAccount,
              to_account: actualToAccount,
              currency_code: currency,
              with_zero_accounts: withZeroAccounts ? "true" : "false",
            }).toString(), { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              };
              return response.json();
            });

            await calc.generate(
              list,
              selCompanyName,
              selFiscalYear,
              company.FiscalYearDescription,
              company.StartDate,
              asOnDate,
              originalCurrency,
              company.CurrencyCode,
              context
            );

          }
        } catch (e) {
          console.error("balanceSheetWizard:onOkClick exception occurred", e);
        }
      });
    } finally {
      setIsValidating(false);
    }
  }

  function onSelectFromDate(date: Date | null | undefined) {
    setAsOnDate(date);
  }

  function onWithZeroAccountsChange(event, checked) {
    setWithZeroAccounts(checked);
  }

  function onOriginalCurrencyChange(event, checked) {
    setOriginalCurrency(checked);
  }

  function onFromAccountChange(event, option, index, value) {
    setFromAccount(option.key);
  }

  function onToAccountChange(event, option, index, value) {
    setToAccount(option.key);
  }

  function onCurrencyChange(event, option, index, value) {
    setCurrency(option.key);
  }

  return (
    <div className="pt-4 col col-12">
      <div className="h-100 document">
        <div onClick={() => handleBackOnClick()} style={{ cursor: "pointer" }} className="d-flex">
          <Icon iconName="Back" className="ms-font-l" style={{ color: setColor("blue") }} />
          <span className="ms-font-l ms-2" style={{ color: setColor("blue") }}>
            {t("back")}
          </span>
        </div>

        <div className="mt-3 mb-2 ms-fontWeight-bold">
          <span>{t("balanceSheet")}</span>
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("company")}
            defaultSelectedKey={selCompanyKey}
            options={companyListOptions}
            onChange={onCompanyChange}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          />
          {selCompanyError ? <span style={{ color: setColor("red") }}>{selCompanyError}</span> : ""}
        </div>

        <div className="mb-2">
          <PMDatePicker
            label={t("fromDate")}
            allowTextInput
            onSelectDate={onSelectFromDate}
            formatDate={onFormatDate}
            parseDateFromString={onParseDateFromString(asOnDate)}
            value={asOnDate}
          />
          {asOnDateError ? <span style={{ color: setColor("red") }}>{asOnDateError}</span> : ""}
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("accountFrom")}
            defaultSelectedKey={fromAccount}
            onChange={onFromAccountChange}
            options={accountListOptions}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
          {fromAccountError ? <span style={{ color: setColor("red") }}>{fromAccountError}</span> : ""}
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("accountTo")}
            defaultSelectedKey={toAccount}
            onChange={onToAccountChange}
            options={accountListOptions}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
          {toAccountError ? <span style={{ color: setColor("red") }}>{toAccountError}</span> : ""}
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("currency")}
            defaultSelectedKey={currency}
            onChange={onCurrencyChange}
            options={currencyListOptions}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
          {currencyError ? <span style={{ color: setColor("red") }}>{currencyError}</span> : ""}
        </div>

        <div className="mb-2">
          <Toggle label={t("originalCurrency")} checked={originalCurrency} onChange={onOriginalCurrencyChange}></Toggle>
        </div>

        <div className="mb-2">
          <Toggle label={t("withZeroAccounts")} checked={withZeroAccounts} onChange={onWithZeroAccountsChange}></Toggle>
        </div>

        <div className="mb-2">
          <DefaultButton onClick={onOkClick}>{t("validate")}</DefaultButton>
        </div>
        {isValidating ? <ProgressIndicator></ProgressIndicator> : ""}
      </div>
    </div>
  );
};
